import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProvidersService, ProviderScheduleDatesResponse } from '../../../../../../js/api';

type ScheduleWindow = { start: string; end: string };

const SCHEDULE_QUERY_KEY = 'provider-schedule';

export const useProviderSchedule = ({ start, end }: ScheduleWindow) => {
  return useQuery({
    queryKey: [SCHEDULE_QUERY_KEY, { start, end }],
    queryFn: () => ProvidersService.providersScheduleDatesRetrieve({ start, end }),
    // The cache is fully managed by useProviderScheduleBulkMutation's
    // onSuccess (setQueriesData with the computed next set). Marking the
    // cache permanently fresh stops react-query from re-issuing the fetch
    // on focus / reconnect / etc — a background refetch would replace the
    // cache with an equivalent-but-new array, churning Date references
    // inside the calendar and repainting selected cells. The first fetch
    // per window still happens on mount.
    //
    // Schedule_type changes invalidate the cache explicitly from
    // useScheduleTypeMutation.onSuccess, so a switch is picked up by the
    // next render despite the Infinity staleTime.
    staleTime: Infinity,
  });
};

export const useProviderScheduleBulkMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { is_blocked: boolean; add: string[]; remove: string[] }) =>
      ProvidersService.providersScheduleBulkCreate({ requestBody: payload }),
    onSuccess: (_data, variables) => {
      // Apply the adds/removes to every cached schedule window directly,
      // instead of invalidating + refetching. Invalidation forces a
      // refetch regardless of staleTime, and the refetch is asynchronous
      // — which leaves a gap where the consumer has cleared its optimistic
      // pending state but the refetched data hasn't arrived yet. During
      // that gap the just-toggled cell renders its OLD state and flashes.
      // Computing the next set locally (current ∪ adds − removes) is safe
      // because the server applies the same operations idempotently.
      //
      // The mutation is mode-scoped: writes only ever touch the array
      // matching its `is_blocked` flag; the inverse array is preserved.
      queryClient.setQueriesData<ProviderScheduleDatesResponse | undefined>(
        { queryKey: [SCHEDULE_QUERY_KEY] },
        (old) => {
          if (!old) return old;
          const targetKey = variables.is_blocked ? 'block_dates' : 'allow_dates';
          const next = new Set(old[targetKey]);
          variables.add.forEach((iso) => next.add(iso));
          variables.remove.forEach((iso) => next.delete(iso));
          return {
            ...old,
            [targetKey]: Array.from(next).sort(),
          };
        }
      );
    },
  });
};
